import { h } from 'preact';
import { useEffect, useMemo, useState, useContext } from 'preact/hooks';
import { sendPostMessage } from 'helpers/post-message.helper';
import { MainWrapper } from 'components-v2/main-wrapper/main-wrapper';
import { Skeleton } from 'components-v2/skeleton/skeleton';
import { LanguageSelector } from 'components-v2/language-selector/language-selector';
import { Header } from 'components-v2/header/header';
import { Footer } from 'components-v2/footer/footer';
import { ApplicationContext, useApplicationSetup } from 'services/use-application-setup';
import { stateStore } from 'global/state.store';
import { useConnect } from 'helpers/use-connect-to-global-store';
import { loadDeferredJs } from 'helpers/scripts-loader';
import { ABOUT_STATE, HOW_IT_WORKS_STATE, INITIAL_PAGE_STATE, REPORT_PAGE_STATE } from 'constants/constants';
import { configStore } from 'global/config.store';
import { loadDeferredCss } from 'helpers/styles-loader';
import { useKeyboardEvents } from 'services/use-keyboard-events';
import { headerStore, HOME_TITLE } from 'global/header.store';
import { useAllyFeatureListeners } from 'services/use-ally-feature-listeners';
import { sessionStore } from 'global/session.store';
import { StatisticsWrapper } from 'services/use-statistics';
import { ReportView } from 'components/report/report';
import { MoveHide } from 'components-v2/move-hide/move-hide';
import { FocusedElementsHandlerWrapper } from 'services/use-focused-element-reader';
import { OversizedWidget } from '../oversized-widget/oversized-widget';
import { Violations } from '../violations/violations';
import { About } from '../about/about';
import { HowItWorksPage } from '../how-it-works/how-it-works-page';
import { Upgrade } from '../upgrade/upgrade';
import { remediationStore, Violations as ViolationsType } from '../../global/remediation.store';
import { LearnMoreButton } from '../learn-more-button/learn-more-button';
import { HowItWorksCta } from '../how-it-works/how-it-works-cta';
import { AllyFeaturesWrapper, AllyFeaturesContext } from '../accessibility-features/use-ally-features';
import { useReaderGuidePosition } from '../accessibility-features/use-reader-guide-position';
import { Profiles } from "../accessibility-features/profiles/profiles";
import { AllyFeaturesList } from "../accessibility-features/ally-features/ally-features-list";
import './initial-page.scss'

declare const applicationConfig: { staticVersion: string };

enum Views {
  InitialPage,
  ReportPage,
  AboutPage,
  HowItWorksPage,
  SkeletonPage,
}

export const InitialPageWrapper = () => {
  const [isAngularLoaded, setIsAngularLoaded] = useState(false);
  const [state, setState] = useConnect(stateStore.state);
  const [isLogged] = useConnect(sessionStore.isLogged$);
  const [isLoggedMember] = useConnect(sessionStore.isLoggedMember$);
  const [violations] = useConnect<ViolationsType>(remediationStore.violations$, null);
  const { allyFeaturesState } = useContext(AllyFeaturesContext);

  const view = useMemo(() => {
    if (state === REPORT_PAGE_STATE) {
      headerStore.setBackStateToHomePage();
      headerStore.title.next('widget.report.title');
      return Views.ReportPage;
    }

    if (state === ABOUT_STATE) {
      headerStore.setBackStateToHomePage();
      // TODO Translate text
      headerStore.title.next(`About UserWay's AI Widget`);
      return Views.AboutPage;
    }

    if (state === HOW_IT_WORKS_STATE) {
      headerStore.setBackStateToHomePage();
      // TODO Translate text
      headerStore.title.next(`About UserWay Widget`);
      return Views.HowItWorksPage;
    }

    if (state === INITIAL_PAGE_STATE) {
      // remove back state on home page
      headerStore.backState.next('');
      headerStore.title.next(HOME_TITLE);

      return Views.InitialPage;
    }

    if (!isAngularLoaded) {
      return Views.SkeletonPage;
    }

    return null;
  }, [state, isAngularLoaded]);

  // This hook triggers only once peg widget initialization
  useEffect(() => {
    setState(INITIAL_PAGE_STATE);

    sendPostMessage({ action: 'appConfigLoaded' });
  }, []);

  useEffect(() => {
    const extendFilePath = (filePath) =>
      // eslint-disable-next-line
      '__CDN__' + `${filePath}?v=${applicationConfig.staticVersion}`;

    if ((view === Views.SkeletonPage || isLogged) && !isAngularLoaded) {
      const appStyles = extendFilePath('frontend/stylesheets/combined.css');
      const mainJs = extendFilePath('frontend/javascripts/combined.js');

      Promise.all([loadDeferredCss(appStyles), loadDeferredJs(mainJs)]).then(() => {
        setIsAngularLoaded(true);
      });
    }
  }, [view, isLogged, isAngularLoaded]);

  const config = useApplicationSetup(true);
  useAllyFeatureListeners(config.sendEvent, config.settings.current);
  useKeyboardEvents(config);
  // react on config updates and pass new values to the configStore
  useEffect(() => {
    configStore.config$.next(config);
  }, [config]);

  useReaderGuidePosition(allyFeaturesState);

  const isUpgradeSectionVisible = useMemo(
    () =>
      isLogged && !isLoggedMember && !config?.paidProductsEnabled?.REMEDIATION && !config.paidAi,
    [isLogged, isLoggedMember, config.paidProductsEnabled, config.paidAi],
  );

  const isViolationSectionVisible = useMemo(
    () => isLogged && !isLoggedMember && !config.paidAi && !!violations.totalCount,
    [isLogged, isLoggedMember, config.paidAi, violations],
  );

  return (
    <ApplicationContext.Provider value={config}>
      <StatisticsWrapper>
        <MainWrapper
          header={<Header close={config.close} />}
          footer={config && !config.tuningsFromServerInProgress && <Footer />}
        >
          <FocusedElementsHandlerWrapper>
            {view === Views.SkeletonPage && <Skeleton />}

            {view === Views.InitialPage && (
              <div className="uwaw-initial-page">
                {isUpgradeSectionVisible && (
                  <LearnMoreButton onClick={() => setState(ABOUT_STATE)} />
                )}

                <HowItWorksCta />

                {isViolationSectionVisible && <Violations />}

                {isUpgradeSectionVisible && !isViolationSectionVisible && <Upgrade />}

                <div className="uwaw-options">
                  <div className="uwaw-options__item">
                    <LanguageSelector />
                  </div>
                  <div className="uwaw-options__item">
                    <Profiles />
                  </div>
                  <div className="uwaw-options__item">
                    <OversizedWidget />
                  </div>
                </div>

                <AllyFeaturesList />

                <div className="uwaw-options">
                  <div className="uwaw-options__item">
                    <MoveHide />
                  </div>
                </div>

              </div>
            )}

            {view === Views.ReportPage && <ReportView />}

            {view === Views.AboutPage && <About />}

            {view === Views.HowItWorksPage && <HowItWorksPage />}

            {/* Angular view */}
            <div ui-view="" />
            {/* ------------ */}
          </FocusedElementsHandlerWrapper>
        </MainWrapper>
      </StatisticsWrapper>
    </ApplicationContext.Provider>
  );
};

export const InitialPage = () => <AllyFeaturesWrapper><InitialPageWrapper/></AllyFeaturesWrapper>;